import { useState, useEffect } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import LandingPage from './components/LandingPage/LandingPage';
import RoomScenePage from './components/RoomscenePage/RoomscenePage';
import VisualizerPage from './components/VisualizerPage/VisualizerPage';

import LoadingPage from './components/LoadingPage/LoadingPage';
import ChamUpload from './components/ChamUpload/ChamUpload';
import ChamProjects from './components/ChamProjects/ChamProjects';
import LoadingScreen from './components/LoadingScreen/LoadingScreen';
import ErrorModal from './components/ErrorModal/ErrorModal';
import DownloadModal from './components/DownloadModal/DownloadModal';

import { visualizerActions, uiActions } from './store';
import { getAllSheetsFromFile } from './Helpers/SheetsHelper';
import { jsonHelper } from './Helpers/JsonHelper';
import { SERIES_IDS } from './Helpers/SeriesHelper';

const router = createBrowserRouter([
  { path: '/', element: <><RoomScenePage /> <ChamProjects /></>, errorElement: <><h1>ERROR</h1></> },
  { path: '/shared/:code/*', element: <LoadingPage/> },
  { path: '/roomscenes', element: <><RoomScenePage />  <ChamProjects /></>  },
  { path: '/visualizer/spec/:specifier/sel/:selString', element: <><VisualizerPage /> <ChamProjects /></>  },
]);

function App() {
  const popUpModalActive = useSelector((state) => state.ui.popUpModalActive);
  const errorModalActive = useSelector((state) => state.ui.errorModalActive);
  const downloadModalActive = useSelector((state) => state.ui.downloadModalActive);
  const masterData = useSelector(s => s.visualizer.productDataBySeries) || {};
  const [dataReady, setDataReady] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        dispatch(uiActions.setVizLoading(true));

        const [
          flooringSheets,
          manningtonCarpet,
          manningtonLvt,
          manningtonBroadloom,
          heterogeneoussheet,
          homogeneoussheet,
          installMethods,
          wallColors,
        ] = await Promise.all([
          getAllSheetsFromFile("/data/flooringData.xlsx"),
          jsonHelper(String(SERIES_IDS.carpet)),
          jsonHelper(String(SERIES_IDS.lvt)),
          jsonHelper(String(SERIES_IDS.broadloom)),
          jsonHelper(String(SERIES_IDS.heterogeneous)),
          jsonHelper(String(SERIES_IDS.homogeneous)),
          jsonHelper(String(SERIES_IDS.installMethods)),
          jsonHelper(String(SERIES_IDS.wallColors)),
        ]);

        if (cancelled) return;

        const byId = (arr) =>
          arr.reduce((acc, obj) => {
            acc[obj.id] = obj;
            return acc;
          }, {});

        dispatch(
          visualizerActions.setProductDataBySeries({
            seriesId: SERIES_IDS.carpet,
            data: byId(manningtonCarpet),
          })
        );
        dispatch(
          visualizerActions.setProductDataBySeries({
            seriesId: SERIES_IDS.lvt,
            data: byId(manningtonLvt),
          })
        );
        dispatch(
          visualizerActions.setProductDataBySeries({
            seriesId: SERIES_IDS.broadloom,
            data: byId(manningtonBroadloom),
          })
        );
        dispatch(
          visualizerActions.setProductDataBySeries({
            seriesId: SERIES_IDS.heterogeneous,
            data: byId(heterogeneoussheet),
          })
        );
        dispatch(
          visualizerActions.setProductDataBySeries({
            seriesId: SERIES_IDS.homogeneous,
            data: byId(homogeneoussheet),
          })
        );
        dispatch(
          visualizerActions.setProductDataBySeries({
            seriesId: SERIES_IDS.wallColors,
            data: byId(wallColors),
          })
        );

        const installByName = installMethods.reduce((acc, obj) => {
          acc[obj.sku] = obj;
          return acc;
        }, {});
        dispatch(
          visualizerActions.setProductDataBySeries({
            seriesId: SERIES_IDS.installMethods,
            data: installByName,
          })
        );

        setDataReady(true);
      } catch (err) {
        console.error("Data bootstrap failed:", err);
        setDataReady(false);
      } finally {
        if (!cancelled) {
          dispatch(uiActions.setVizLoading(false));
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  // detect ?style_code after data is ready
  useEffect(() => {
    if (!dataReady) return;

    const params = new URLSearchParams(window.location.search);

    if (params.has("style_code")) {
      const styleCode = params.get("style_code"); 

      console.log("style_code param found:", styleCode);

      // TODO: your processing here, e.g.:
      // dispatch(visualizerActions.setActiveStyleCode(styleCode));
      // or navigate somewhere, etc.
      const search = window.location.search || "";
      const hash = window.location.hash || "";

      const url =
        `/visualizer/spec/Mannington%5CImages%5CStandard%5CEducation_Classroom_9/sel/-1` +
        search +
        hash;

      // 👈 navigate using the router instance, not the hook
      router.navigate(url);

      dispatch(visualizerActions.setActiveProductMenuTab("flooring"));
      dispatch(visualizerActions.setActiveFlooringSubMenuTab("Modular Carpet"));
      dispatch(visualizerActions.setActiveFlooringSeriesIds([SERIES_IDS.carpet]));

      
      let product = masterData[SERIES_IDS.carpet][5967217];
      console.log(product)

      dispatch(visualizerActions.setAppliedProduct({
            location: 'chamViz',
            surface: 'Floor',
            data: {
                colorway: product,
                selStrings: [],
                size: null,
                install: 5983716,
            },
        }));

    }
  }, [dataReady, dispatch]);

  // detect ?sids after data is ready
  useEffect(() => {
    if (!dataReady) return;

    const params = new URLSearchParams(window.location.search);

    if (params.has("sids")) {
      const sidsParam = params.get("sids");
      const sids = sidsParam.split(',').map(id => parseInt(id.trim(), 10));

      console.log("sids param found:", sids);

      // Map sids to submenu tab name
      let subMenuTab = null;
      let seriesIds = sids;

      // Check which submenu tab matches the sids
      if (sids.includes(SERIES_IDS.carpet)) {
        subMenuTab = 'Modular Carpet';
        seriesIds = [SERIES_IDS.carpet];
      } else if (sids.includes(SERIES_IDS.broadloom)) {
        subMenuTab = 'Broadloom Carpet';
        seriesIds = [SERIES_IDS.broadloom];
      } else if (sids.includes(SERIES_IDS.lvt)) {
        subMenuTab = 'LVT';
        seriesIds = [SERIES_IDS.lvt];
      } else if (sids.includes(SERIES_IDS.heterogeneous) || sids.includes(SERIES_IDS.homogeneous)) {
        subMenuTab = 'Resilient Sheet';
        seriesIds = [SERIES_IDS.heterogeneous, SERIES_IDS.homogeneous];
      }

      if (subMenuTab) {
        dispatch(visualizerActions.setActiveProductMenuTab("flooring"));
        dispatch(visualizerActions.setActiveFlooringSubMenuTab(subMenuTab));
        dispatch(visualizerActions.setActiveFlooringSeriesIds(seriesIds));
      }
    }
  }, [dataReady, dispatch]);

  // detect ?error after data is ready
  useEffect(() => {
    if (!dataReady) return;

    const params = new URLSearchParams(window.location.search);

    if (params.has("error")) {
      const errorType = params.get("error");

      if (errorType === "notfound") {
        dispatch(uiActions.setErrorModalMessage("The product selected could not be found."));
        dispatch(uiActions.setErrorModalActive(true));
      }
    }
  }, [dataReady, dispatch]);

  return (
    <div className={`App ${popUpModalActive ? "blur" : ""}`}>
      {dataReady ? (
        <>
          <RouterProvider router={router} />
          <ChamUpload />
          {errorModalActive && <ErrorModal />}
          {downloadModalActive && <DownloadModal />}
        </>
      ) : (
        <LoadingScreen />
      )}
    </div>
  );
}

export default App;
